import React, { useState, useRef } from "react"
import { Theme } from "@mui/material/styles"
import { Box, Button, Menu, MenuItem, Stack, Typography, IconButton } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowUpFromBracket, faPrint, faCopy, faBars } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { tailwindPalette } from "../../../../theme/tailwindPalette"
import { USER_REPORTS_ROUTE } from "../../../../../constants/routes"
import { faCircleXmark } from "@fortawesome/pro-thin-svg-icons"
import { useAppSelector } from "../../../../store/storeHooks"
import { selectUserId } from "../../../../login/loginSelectors"
import { useNavigate } from "react-router-dom"

const useStyles = makeStyles()((theme: Theme) => ({
    buttonContainer: {
        display: "flex",
        gap: "0.5rem",
        [theme.breakpoints.down("sm")]: {
            display: "none",
        },
    },
    button: {
        display: "flex",
        alignItems: "center",
        gap: "0.25rem",
        padding: "0.5rem 1rem",
        fontSize: "0.875rem",
        fontWeight: 500,
        color: "#1E293B",
        border: "none",
        borderRadius: "0.5rem",
        backgroundColor: "transparent",
        textTransform: "none",
        "&:hover": {
            backgroundColor: tailwindPalette.gray[100],
        },
        "@media print": {
            display: "none",
        },
        [theme.breakpoints.down("sm")]: {
            backgroundColor: "none",
            gap: "2.25rem",
            width: "100%",
            justifyContent: "start",
            padding: 0,
            "&:hover": {
                backgroundColor: "transparent",
            },
        },
    },
    menu: {
        "& .MuiPaper-root": {
            borderRadius: "0.5rem",
            boxShadow: theme.shadows[5],
            padding: "1rem",
            width: "20rem",
            backgroundColor: "white",
        },
    },
    mobileMenu: {
        "& .MuiPaper-root": {
            borderRadius: "0.25rem",
            boxShadow: theme.shadows[5],
            width: "11.25rem",
            backgroundColor: "white",
        },
        "& .MuiMenu-list": {
            padding: "1.25rem 1rem",
        },
        "@media print": {
            display: "none",
        },
    },
    printMenuIcon: {
        fontSize: "1rem",
    },
    printMenu: {
        "@media print": {
            display: "none",
        },
    },
    menuContent: {
        display: "flex",
        flexDirection: "column",
        gap: "0.5rem",
    },
    menuTitle: {
        fontSize: "1rem",
        fontWeight: 600,
        color: "#1E293B",
    },
    menuDescription: {
        fontSize: "0.875rem",
        color: tailwindPalette.slate[600],
    },
    linkContainer: {
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        border: "1px solid gray",
        padding: "0.5rem",
        borderRadius: "0.5rem",
    },
    link: {
        fontSize: "0.875rem",
        color: tailwindPalette.blue[600],
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    copyButton: {
        padding: "0.25rem",
    },
    burgerMenu: {
        [theme.breakpoints.up("sm")]: {
            display: "none",
        },
        "@media print": {
            display: "none",
        },
    },
}))

type SharePrintsButtonsProps = Readonly<{
    onClose?: () => void
    copyUrl?: string
    hasShare?: boolean
    hasClose?: boolean
    onPrintWithChatHistory?: () => void
}>

export const SharePrintButtons: React.FC<SharePrintsButtonsProps> = ({
    onClose,
    copyUrl = "",
    hasShare = true,
    hasClose = true,
    onPrintWithChatHistory,
}) => {
    const { classes } = useStyles()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [mobileAnchorEl, setMobileAnchorEl] = useState<null | HTMLElement>(null)
    const [printAnchorEl, setPrintAnchorEl] = useState<null | HTMLElement>(null)
    const shareButtonRef = useRef<HTMLButtonElement>(null)
    const userId = useAppSelector(selectUserId)
    const navigate = useNavigate()
    const isAuthenticated = Boolean(userId)

    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    const handlePrint = () => {
        window.print()
    }

    const handlePrintClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (onPrintWithChatHistory) {
            setPrintAnchorEl(event.currentTarget)
            return
        }
        handlePrint()
    }

    const handlePrintMenuClose = () => {
        setPrintAnchorEl(null)
    }

    const handlePrintWithChatHistory = () => {
        setPrintAnchorEl(null)
        setMobileAnchorEl(null)
        onPrintWithChatHistory?.()
    }

    const handlePrintWithoutChatHistory = () => {
        setPrintAnchorEl(null)
        setMobileAnchorEl(null)
        handlePrint()
    }

    const handleCopy = async () => {
        await navigator.clipboard.writeText(copyUrl)
        handleClose()
    }

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMobileAnchorEl(event.currentTarget)
    }

    const handleMenuClose = () => {
        setMobileAnchorEl(null)
    }

    return (
        <Box>
            <Box className={classes.buttonContainer}>
                {hasShare && (
                    <Button className={classes.button} onClick={handleOpen} ref={shareButtonRef}>
                        <FontAwesomeIcon icon={faArrowUpFromBracket} style={{ fontSize: "1rem" }} />
                        <LocalizedStrict id="user-order-receipt-page-share-button">
                            <>Share</>
                        </LocalizedStrict>
                    </Button>
                )}
                <Button className={classes.button} onClick={handlePrintClick}>
                    <FontAwesomeIcon icon={faPrint} style={{ fontSize: "1rem" }} />
                    <LocalizedStrict id="user-order-receipt-page-print-button">
                        <>Print</>
                    </LocalizedStrict>
                </Button>
                <Menu
                    anchorEl={printAnchorEl}
                    open={Boolean(printAnchorEl)}
                    onClose={handlePrintMenuClose}
                    className={classes.printMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <LocalizedStrict id="order-chat-history-print-without-button">
                        <MenuItem onClick={handlePrintWithoutChatHistory}>Print</MenuItem>
                    </LocalizedStrict>
                    <LocalizedStrict id="order-chat-history-print-with-button">
                        <MenuItem onClick={handlePrintWithChatHistory}>Print with chat history</MenuItem>
                    </LocalizedStrict>
                </Menu>
                {isAuthenticated && hasClose && (
                    <IconButton onClick={onClose}>
                        <FontAwesomeIcon icon={faCircleXmark} />
                    </IconButton>
                )}
            </Box>
            <Box className={classes.burgerMenu}>
                <IconButton onClick={handleMenuOpen}>
                    <FontAwesomeIcon icon={faBars} />
                </IconButton>
                <Menu
                    anchorEl={mobileAnchorEl}
                    open={Boolean(mobileAnchorEl)}
                    onClose={handleMenuClose}
                    className={classes.mobileMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    {onPrintWithChatHistory ? (
                        <Stack spacing="1rem">
                            <Button className={classes.button} onClick={handlePrintWithoutChatHistory}>
                                <FontAwesomeIcon icon={faPrint} className={classes.printMenuIcon} />
                                <LocalizedStrict id="order-chat-history-print-without-button">
                                    <Typography variant="body1">Print</Typography>
                                </LocalizedStrict>
                            </Button>
                            <Button className={classes.button} onClick={handlePrintWithChatHistory}>
                                <FontAwesomeIcon icon={faPrint} className={classes.printMenuIcon} />
                                <LocalizedStrict id="order-chat-history-print-with-button">
                                    <Typography variant="body1">Print with chat history</Typography>
                                </LocalizedStrict>
                            </Button>
                        </Stack>
                    ) : (
                        <Button className={classes.button} onClick={handlePrint}>
                            <FontAwesomeIcon icon={faPrint} style={{ fontSize: "1rem" }} />
                            <LocalizedStrict id="user-order-receipt-page-print-button">
                                <Typography variant="body1">Print</Typography>
                            </LocalizedStrict>
                        </Button>
                    )}
                </Menu>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                className={classes.menu}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Box className={classes.menuContent}>
                    <LocalizedStrict id="user-order-receipt-page-share-modal-title">
                        <Typography className={classes.menuTitle}>Share receipt</Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="user-order-receipt-page-share-modal-subtitle">
                        <Typography className={classes.menuDescription}>
                            Share this link to let others view the receipt.
                        </Typography>
                    </LocalizedStrict>
                    <Box className={classes.linkContainer}>
                        <Typography className={classes.link}>{window.location.href}</Typography>
                        <IconButton className={classes.copyButton} onClick={handleCopy}>
                            <FontAwesomeIcon icon={faCopy} style={{ fontSize: "1rem" }} />
                        </IconButton>
                    </Box>
                </Box>
            </Menu>
        </Box>
    )
}
